/**
 * Erreurs de lecture → écran lisible.
 *
 * Les codes viennent du moteur (Worker ou thread principal). L'UI ne devine
 * jamais : chaque code a un titre en français, et le message technique du
 * moteur reste affiché en second plan.
 */
import { h } from './dom';

export interface UiError {
  code: string;
  message: string;
}

const TITLES: Record<string, string> = {
  NO_AF_ATTACHMENT: 'Aucune facture électronique dans ce fichier',
  NO_XML_ATTACHMENT: 'Aucune facture XML reconnue dans ce PDF',
  ZUGFERD1_UNSUPPORTED: 'Format ZUGFeRD 1.0 non pris en charge',
  NOT_AN_INVOICE: 'Ce document n’est pas une facture',
  ENCRYPTED_PDF: 'PDF protégé par mot de passe',
  PDF_INVALID: 'PDF illisible',
  XML_INVALID: 'XML illisible',
  TIMEOUT: 'Lecture trop longue',
  WORKER_ERROR: 'Le moteur de lecture s’est arrêté',
  DISPOSED: 'Lecture interrompue',
  UNSUPPORTED_FILE: 'Format de fichier non pris en charge',
  FILE_TOO_LARGE: 'Fichier trop volumineux',
  EMPTY_FILE: 'Fichier vide',
};

const HINTS: Record<string, string> = {
  NO_AF_ATTACHMENT: 'Un PDF de facture électronique contient normalement une pièce jointe XML (Factur-X, ZUGFeRD, XRechnung). Ce fichier n’en contient pas.',
  NO_XML_ATTACHMENT: 'Les pièces jointes présentes ne sont pas une facture XML reconnue (une commande ORDER-X, par exemple).',
  ZUGFERD1_UNSUPPORTED: 'ZUGFeRD 1.0 est un format obsolète, remplacé par Factur-X / ZUGFeRD 2. Demandez au vendeur une facture Factur-X, XRechnung ou UBL.',
  NOT_AN_INVOICE: 'Il s’agit peut-être d’une commande ou d’un autre document métier.',
  ENCRYPTED_PDF: 'Un PDF protégé ne peut pas être lu sans mot de passe. PDF/A interdit le chiffrement : demandez une version non protégée.',
  FILE_TOO_LARGE: 'La limite est de 20 Mo par fichier.',
  EMPTY_FILE: 'Le fichier fait 0 octet.',
  UNSUPPORTED_FILE: 'Formats acceptés : .pdf et .xml.',
};

export function toUiError(err: unknown): UiError {
  if (err && typeof err === 'object') {
    const candidate = err as { code?: unknown; message?: unknown; name?: unknown };
    const code = typeof candidate.code === 'string' ? candidate.code : typeof candidate.name === 'string' ? candidate.name : 'UNKNOWN';
    const message = typeof candidate.message === 'string' ? candidate.message : 'Erreur inconnue';
    return { code, message };
  }
  return { code: 'UNKNOWN', message: String(err) };
}

export interface ErrorViewOptions {
  filename?: string;
  onRetry: () => void;
}

export function renderError(error: UiError, options: ErrorViewOptions): HTMLElement {
  const legacy = error.code === 'ZUGFERD1_UNSUPPORTED';
  const title = TITLES[error.code] ?? 'Lecture impossible';
  const hint = HINTS[error.code] ?? 'Vérifiez que le fichier est bien une facture électronique (PDF avec XML joint, ou XML Factur-X / XRechnung / UBL).';

  return h(
    'article',
    { class: 'invoice invoice--error', 'aria-labelledby': 'error-title' },
    h(
      'header',
      { class: 'invoice__head' },
      h(
        'div',
        { class: 'badges' },
        // §8.3 : ZUGFeRD 1.0 a son propre badge, en ton avertissement.
        legacy ? h('span', { class: 'badge badge--warning', role: 'img', 'aria-label': 'Profil ZUGFeRD 1.0 déclaré' }, 'ZUGFeRD 1.0') : null,
        h('span', { class: 'badge badge--danger badge--doctype' }, 'Lecture impossible'),
      ),
      h('h2', { class: 'invoice__title', id: 'error-title', tabindex: '-1' }, title),
      options.filename ? h('p', { class: 'file-badge' }, h('span', { class: 'file-badge__name' }, options.filename), h('span', { class: 'file-badge__state' }, '· non modifié')) : null,
    ),
    h('p', { class: 'error__hint' }, hint),
    h('p', { class: 'error__message' }, error.message),
    h('p', { class: 'muted small' }, `Code : ${error.code}`),
    h(
      'p',
      { class: 'error__actions' },
      h('button', { type: 'button', class: 'button button--primary js-retry' }, 'Choisir une autre facture'),
    ),
  );
}
